// TODO: remove unused.
import create from 'zustand';
import { tokenName, tokenV3Name, userName, DEFAULT_IDLE_TIMEOUT } from 'config/constants';
import { configurePersist } from 'zustand-persist';
import { translations } from 'translations';
import { IUserTeam } from '@cincel/client';
import { hasAnySession, getSessionTokenV3, getLegacyToken, getStorage, getSessionUser } from 'helpers/sessionContext';

const { persist, purge } = configurePersist({
  storage: getStorage(), // dynamic storage based on context (Guest=Session, Client=Local)
  rootKey: 'root', // optional, default value is `root`
});

interface SessionStore {
  user: any;
  mobileSidebarOpen: boolean;
  isLoggedIn: any;
  token: any;
  tokenV3: any;
  defaultOrganization: any;
  invitations: any;
  currentOrganization: any;
  organizations: any;
  organizationImages: any;
  currentPlan: any;
  accountLimits: any;
  idleTimeout: number; // in seconds
  isV3Expired: boolean;

  language: any;
  teams: any;
  team: any;
  orgsToTeamsMap: any;
  setUser: any;
  setMobileSidebarOpen: (v: boolean) => void;
  setIsLoggedIn: any;
  setToken: any;
  setTokenV3: any;
  setCurrentOrganization: any;
  setOrganizations: any;
  setOrganizationImages: any;
  setCurrentPlan: any;
  setAccountLimits: any;
  setIdleTimeout: (seconds: number) => void;
  setInvitations: any;
  setIsV3Expired: (v: boolean) => void;

  getOrganization: any;
  updateOrganization: any;
  setLanguage: any;
  setTeams: any;
  clean: any;
  showBasicCertificates: any;
  credits: any;
  setShowBasicCertificates: (value: boolean) => void;
  setTeam: (team: IUserTeam | null) => void;
  setOrgsToTeamsMap: (v: any) => void;
  setCredits: (v: any) => void;
}

export const useSession = create<SessionStore>(
  persist(
    {
      key: 'cincelSession-data', // unique name
      denylist: ['organizationImages', 'organizations', 'mobileSidebarOpen'],
    },
    (set, get) => ({
      user: getSessionUser(),
      mobileSidebarOpen: false,
      isLoggedIn: hasAnySession(),
      token: getLegacyToken(),
      tokenV3: getSessionTokenV3(),
      defaultOrganization: {
        name: translations.ORGANIZATIONS.MY_ORGANIZATION,
        imagotype: '/img/defaultOrg.png',
        owner: false,
        active: true,
        id: 0,
        translation: true,
      },
      invitations: [],
      currentOrganization: {},
      organizations: [],
      currentPlan: {},
      accountLimits: {},
      idleTimeout: DEFAULT_IDLE_TIMEOUT, // Default 1h
      isV3Expired: false,
      organizationImages: [],
      teams: [],
      team: null,
      orgsToTeamsMap: {},
      language:
        ((window.navigator as any).userLanguage ||
          window.navigator.language) === 'en-US'
          ? 'en'
          : (window.navigator as any).userLanguage || window.navigator.language,
      showBasicCertificates: false,
      credits: null,
      setShowBasicCertificates: (showBasicCertificates) => {
        return set(() => ({
          showBasicCertificates,
        }));
      },
      setUser: (user) => {
        return set(() => ({
          user: {
            ...user,
            organizations: undefined,
          },
        }));
      },
      setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),
      setIsLoggedIn: (isLoggedIn) => {
        return set(() => ({
          isLoggedIn,
        }));
      },
      setToken: (token) => {
        return set(() => ({
          token,
        }));
      },
      setTokenV3: (tokenV3) => {
        return set(() => ({ tokenV3 }));
      },
      setCurrentOrganization: (currentOrganization) => {
        return set(() => ({
          currentOrganization,
        }));
      },
      setOrganizations: (organizations) => {
        const newOraganizations = organizations.map((org) => ({
          ...org,
          imagotype: null,
        }));
        return set(() => ({
          organizations: newOraganizations,
        }));
      },
      setOrganizationImages: (organizationImages) => {
        return set(() => ({
          organizationImages,
        }));
      },
      setCurrentPlan: (currentPlan) => {
        const newPlan = { ...currentPlan };

        return set(() => ({
          currentPlan: {
            ...newPlan,
            name: 'Start',
          },
        }));
      },
      setAccountLimits: (accountLimits) => {
        return set(() => ({
          accountLimits,
        }));
      },
      setInvitations: (invitations) => {
        return set(() => ({
          invitations,
        }));
      },
      setIsV3Expired: (isV3Expired) => set({ isV3Expired }),
      setIdleTimeout: (seconds) => {
        // Enforce limits: 5m (300s) min, 8h (28800s) max
        const enforced = Math.min(Math.max(seconds, 300), 28800);
        set({ idleTimeout: enforced });
      },

      getOrganization: (id) => {
        const organizations = (get() as any).organizations;
        const organization = organizations.find((org) => org.id === id);

        return organization;
      },
      updateOrganization: (id, data) => {
        const organizations = (get() as any).organizations;
        const organizationIndex = organizations.findIndex(
          (org) => org.id === parseInt(id, 10),
        );
        organizations[organizationIndex] = { ...data };
        return set(() => ({
          organizations,
        }));
      },
      setLanguage: (language) => {
        return set(() => ({
          language,
        }));
      },
      setTeams: (teams) => {
        return set(() => ({
          teams,
        }));
      },
      setTeam: (team) => {
        return set(() => ({ team }));
      },
      setCredits: (credits) => {
        return set(() => ({ credits }));
      },
      setOrgsToTeamsMap: (orgsToTeamsMap) => {
        return set(() => ({ orgsToTeamsMap }));
      },
      clean: () =>
        set({
          user: null,
          isLoggedIn: false,
          token: null,
          tokenV3: null,
          defaultOrganization: {
            name: translations.ORGANIZATIONS.MY_ORGANIZATION,
            imagotype: '/img/defaultOrg.png',
            owner: false,
            active: true,
            id: 0,
            translation: true,
          },
          invitations: [],
          currentOrganization: {},
          organizations: [],
          currentPlan: {},
          accountLimits: {},

          language: 'es',
          team: null,
          orgsToTeamsMap: {},
          credits: null,
          isV3Expired: false,
          idleTimeout: DEFAULT_IDLE_TIMEOUT,
        }),
    }),
  ),
);
